// src/prototype/pages/EAPrintableBoardRationalePage.tsx
// Board Rationale — Governance Approval Brief
// CI Pass: decision callout added, section headers tightened, risk section
// converted to card grid, copy tightened throughout, recommendation
// strengthened. Shell, route, and print framework unchanged.

import React from 'react';
import { DEMO_CLIENT } from '../config/demoClient';
import { demoMonthYear } from '../config/demoDates';
import {
  EAUseCaseArtifactShell,
  type SummaryMetric,
  type ArtifactSection,
} from '../components/ea_reports/use_case/EAUseCaseArtifactShell';
import { CANON, CANON_CONFIDENCE_BASIS } from '../roi/roiCanon';

// ─── Summary Strip ────────────────────────────────────────────────────────────
//
// Financial figures come from the ROI engine (src/prototype/roi). Investment is
// stated as the resolved canon — implementation plus operating, totalled — rather
// than the former "$2.1M – $3.4M total implementation cost".

const SUMMARY_METRICS: SummaryMetric[] = [
  { label: 'Strategic Priority',     value: 'High',                              sub: 'enterprise initiative'        },
  { label: 'Required Investment',    value: CANON.totalCost3Yr,                  sub: 'total 3-year cost'            },
  { label: 'Estimated Annual Value', value: CANON.annualValueRange, accent: true, sub: 'conservative–aggressive span' },
  { label: 'Payback Period',         value: CANON.basePayback,      accent: true, sub: 'base case'                    },
  { label: 'Recommendation',         value: 'Recommended for First-Wave',        sub: 'governance consideration'     },
];

// ─── Decision Callout ─────────────────────────────────────────────────────────

const DecisionCallout: React.FC = () => (
  <div style={{
    border: '1.5px solid #18365E',
    borderLeft: '5px solid #FF6E4C',
    borderRadius: '6px',
    background: '#F0F4F9',
    padding: '14px 18px',
    pageBreakInside: 'avoid',
    breakInside: 'avoid',
  }}>
    <div style={{
      fontSize: '7pt',
      fontWeight: 700,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: '#FF6E4C',
      marginBottom: '6px',
    }}>
      Board Decision Context
    </div>
    <p style={{ fontSize: '9.5pt', color: '#18365E', lineHeight: 1.6, margin: 0, fontWeight: 500 }}>
      Care Coordination and Transition Management is recommended for approval as a first-wave
      strategic initiative based on strong financial return, short payback period, and clear
      alignment with value-based care priorities.
    </p>
  </div>
);

// ─── Risk Cards ───────────────────────────────────────────────────────────────

const RISKS = [
  {
    risk:       'Data Integration Complexity',
    impact:     'Delays in model deployment if upstream data feeds are incomplete',
    mitigation: 'Data readiness assessment in Phase 0; prioritize highest-coverage sources first',
  },
  {
    risk:       'Workflow Adoption',
    impact:     'Care management teams may not consistently act on risk alerts',
    mitigation: 'Embed alerts in existing workflows; designate clinical champions per unit',
  },
  {
    risk:       'Change Management Burden',
    impact:     'Cross-functional coordination across care management, informatics, and operations creates friction',
    mitigation: 'Assign dedicated program manager; establish steering cadence with clinical and operational leads',
  },
  {
    risk:       'Intervention Coverage',
    impact:     'Phase 1 scope limited to inpatient discharge pathways',
    mitigation: 'Expand to ED and SNF pathways in Phase 2 following inpatient stabilization',
  },
];

const RiskCards: React.FC = () => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
    marginTop: '4px',
  }}>
    {RISKS.map((row, i) => (
      <div key={i} style={{
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        background: '#ffffff',
        padding: '12px 14px',
        pageBreakInside: 'avoid',
      }}>
        <div style={{
          fontSize: '7.5pt',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: '#18365E',
          marginBottom: '8px',
          borderBottom: '1px solid #e5e7eb',
          paddingBottom: '6px',
        }}>
          {row.risk}
        </div>
        <div style={{ marginBottom: '8px' }}>
          <div style={{
            fontSize: '6.5pt',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: '#717182',
            marginBottom: '3px',
          }}>
            Impact
          </div>
          <div style={{ fontSize: '8.5pt', color: '#374151', lineHeight: 1.5 }}>
            {row.impact}
          </div>
        </div>
        <div>
          <div style={{
            fontSize: '6.5pt',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: '#717182',
            marginBottom: '3px',
          }}>
            Mitigation
          </div>
          <div style={{ fontSize: '8.5pt', color: '#374151', lineHeight: 1.5 }}>
            {row.mitigation}
          </div>
        </div>
      </div>
    ))}
  </div>
);

// ─── Sections ─────────────────────────────────────────────────────────────────

const SECTIONS: ArtifactSection[] = [
  {
    title: '',
    content: <DecisionCallout />,
  },
  {
    title: 'Strategic Rationale',
    content: (
      <>
        <p>
          Care Coordination and Transition Management addresses one of the most significant
          opportunities to reduce avoidable utilization and strengthen performance in
          value-based care arrangements. Poorly managed transitions are a leading driver of
          preventable readmissions — and the costs fall directly on the health system under
          bundled payment and shared savings contracts.
        </p>
        <p>
          This initiative advances a health system's value-based care strategy by improving
          care coordination efficiency, reducing avoidable readmissions, and strengthening
          performance on payer contract quality metrics.
        </p>
      </>
    ),
  },
  {
    title: 'Financial Impact Overview',
    content: (
      <p>
        The initiative requires <strong>{CANON.investmentSchedule}</strong> — a{' '}
        <strong>{CANON.totalCostLabel}</strong> — and is projected to generate{' '}
        <strong>{CANON.annualValueRange} in annual value</strong>, with expected payback in
        approximately <strong>{CANON.basePayback}</strong> and{' '}
        <strong>{CANON.baseNetValue3Yr}</strong> of net value over three years at a{' '}
        {CANON.discountRate} discount rate. Model confidence is {CANON.confidencePct}{' '}
        ({CANON_CONFIDENCE_BASIS}). The primary value drivers are reduced
        inpatient readmissions and improved care coordination efficiency across transition-risk
        populations. This initiative ranks among the strongest return profiles in the evaluated
        use case portfolio.{' '}
        The full financial model — including scenario analysis, payback outlook, and key assumptions —
        is detailed in the CFO ROI Summary. Complete initiative evaluation is available in the Use Case Profile.
      </p>
    ),
  },
  {
    title: 'Execution Readiness',
    content: (
      <p>
        The operational foundation for this initiative is well-established. Existing care
        management, discharge planning, and analytics teams provide the operational foundation for
        Phase 1. Required data inputs — encounter history, discharge records, and risk stratification
        feeds — are available within current systems. No significant new infrastructure is required.
        Implementation complexity is rated low relative to the broader initiative portfolio.
      </p>
    ),
  },
  {
    title: 'Key Risks & Mitigations',
    content: <RiskCards />,
  },
  {
    title: 'Board Recommendation',
    content: (
      <div style={{
        background: '#18365E',
        borderRadius: '8px',
        padding: '20px 24px',
        borderTop: '3px solid #FF6E4C',
      }}>
        <div style={{
          fontSize: '7pt',
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: '#FF6E4C',
          marginBottom: '10px',
        }}>
          Board Recommendation
        </div>
        <p style={{
          fontSize: '10.5pt',
          fontWeight: 700,
          color: '#ffffff',
          lineHeight: 1.5,
          margin: 0,
        }}>
          Care Coordination and Transition Management is recommended for approval as a first-wave strategic initiative.
        </p>
        <p style={{
          fontSize: '9pt',
          fontWeight: 400,
          color: 'rgba(255,255,255,0.85)',
          lineHeight: 1.7,
          margin: '12px 0 0',
        }}>
          The initiative demonstrates strong financial return, short payback period, and clear
          alignment with enterprise strategic priorities. Investment of{' '}
          <strong style={{ color: '#ffffff' }}>{CANON.totalCost3Yr}</strong> over three years{' '}
          ({CANON.investmentSchedule}) is projected to deliver{' '}
          <strong style={{ color: '#ffffff' }}>{CANON.annualValueRange}</strong> in annual value, recovering
          in <strong style={{ color: '#ffffff' }}>{CANON.basePayback}</strong>. Operational infrastructure is
          in place and implementation complexity is manageable. Board authorization is recommended
          to enable Phase 1 planning and resourcing.
        </p>
      </div>
    ),
  },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function EAPrintableBoardRationalePage() {
  return (
    <EAUseCaseArtifactShell
      title="Board Rationale"
      subtitle="Governance Approval Brief"
      clientName={DEMO_CLIENT.name}
      useCaseName="Care Coordination and Transition Management"
      reportDate={demoMonthYear()}
      summaryMetrics={SUMMARY_METRICS}
      sections={SECTIONS}
      manualPrint
      printFilename="Board Rationale — Care Coordination and Transition Management"
      printLabel="Board Rationale — Print Preview"
    />
  );
}